/**
 * Plan §8.3 / §20.5 #7 — in-memory ring buffer of recent market-update payloads
 * per (instance, ticker). Fed by `HubProvider`'s `marketUpdated` handler;
 * consumed by the instance price chart on the dashboard card + detail modal.
 *
 * Keyed by ticker as well as instance because every instance runs TWO live
 * market loops (one per team's Kalshi market) and the two books quote
 * complementary prices — folding them into one series is the "price keeps
 * flipping" bug. Consumers pick the buffer for the instance's mark ticker;
 * the other side's buffer keeps accumulating so a mid-game side selection
 * already has history when the mark flips to the traded ticker.
 *
 * Deliberately tiny: no library, no global Zustand/Redux, no React Query
 * persistence. The hub feed is already client-only (lost on reload) — this
 * buffer matches that lifetime exactly. REST history from
 * `GET /api/instances/{id}/market-history` is merged at render time by the
 * chart, never written into this buffer.
 */
import { useSyncExternalStore } from 'react'

export type MarketPoint = {
    at: number
    bestBidCents: number | null
    bestAskCents: number | null
}

const MAX_POINTS_PER_BUFFER = 240

const buffers = new Map<string, MarketPoint[]>()
const listeners = new Set<() => void>()

const bufferKey = (instanceId: string, ticker: string) => `${instanceId}|${ticker}`

const emit = () => {
    for (const l of listeners) l()
}

export const pushMarketPoint = (instanceId: string, ticker: string, point: MarketPoint) => {
    const key = bufferKey(instanceId, ticker)
    const prev = buffers.get(key) ?? []
    const next = [...prev, point]
    if (next.length > MAX_POINTS_PER_BUFFER) {
        next.splice(0, next.length - MAX_POINTS_PER_BUFFER)
    }
    buffers.set(key, next)
    emit()
}

const subscribe = (cb: () => void) => {
    listeners.add(cb)
    return () => listeners.delete(cb)
}

const EMPTY: MarketPoint[] = []

export const useMarketHistory = (
    instanceId: string | null,
    ticker: string | null,
): MarketPoint[] => {
    const getSnapshot = () =>
        instanceId && ticker ? (buffers.get(bufferKey(instanceId, ticker)) ?? EMPTY) : EMPTY
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/** Wipes every buffer. An account switch calls this alongside the query
 *  cache clear so no price series from one account survives into the next. */
export const clearMarketHistory = () => {
    buffers.clear()
    emit()
}
